import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  Progress,
  Avatar,
  Tooltip,
  Divider,
} from "antd";
import {
  FaLightbulb,
  FaSearch,
  FaEdit,
  FaPlus,
  FaCalendarAlt,
  FaBullseye,
  FaBuilding,
  FaChartLine,
  FaFilter,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";
import "./Iniciativas.css"; // CSS opcional para ajustes finos

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE ADICIONAR/EDITAR INICIATIVA
───────────────────────────────────────────────────────────────────────────── */
const IniciativaFormModal = ({
  open,
  onClose,
  onSuccess,
  editingIniciativa,
  userStored,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [objetivos, setObjetivos] = useState([]);
  const [direcoes, setDirecoes] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (open) {
      loadOptions();
      if (editingIniciativa) {
        form.setFieldsValue({
          titulo: editingIniciativa.titulo,
          descricao: editingIniciativa.descricao,
          inicio: editingIniciativa.inicio ? dayjs(editingIniciativa.inicio) : null,
          fim: editingIniciativa.fim ? dayjs(editingIniciativa.fim) : null,
          id_objectivo: editingIniciativa.id_objectivo,
          id_direcao: editingIniciativa.id_direcao || userStored.direcao,
        });
      } else {
        form.resetFields();
        // Preencher direção padrão (do usuário logado) se existir
        if (userStored.direcao) {
          form.setFieldsValue({ id_direcao: userStored.direcao });
        }
      }
    }
  }, [open, editingIniciativa, form, userStored]);

  const loadOptions = async () => {
    try {
      setLoadingOptions(true);
      const [objs, dirs] = await Promise.all([
        Api.ListObjetivo().catch(() => []),
        Api.ListDirecao().catch(() => []),
      ]);
      setObjetivos(Array.isArray(objs) ? objs : []);
      setDirecoes(Array.isArray(dirs) ? dirs : []);
    } catch {
      toast.error("Erro ao carregar objetivos ou direções.");
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        titulo: values.titulo,
        descricao: values.descricao,
        inicio: values.inicio ? values.inicio.format("YYYY-MM-DD") : null,
        fim: values.fim ? values.fim.format("YYYY-MM-DD") : null,
        id_objectivo: values.id_objectivo,
        id_direcao: values.id_direcao,
      };

      if (editingIniciativa) {
        await Api.AlterIniciativa({
          ...payload,
          id: editingIniciativa.id,
        });
        toast.success("Iniciativa atualizada com sucesso!");
      } else {
        await Api.AddInicitiva(payload);
        toast.success("Iniciativa criada com sucesso!");
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha todos os campos obrigatórios.");
      } else {
        toast.error(
          editingIniciativa
            ? "Erro ao atualizar iniciativa."
            : "Erro ao criar iniciativa."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={650}
      title={
        <Space>
          <FaLightbulb style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>
            {editingIniciativa ? "Editar Iniciativa" : "Nova Iniciativa"}
          </span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          {editingIniciativa ? "Atualizar" : "Criar Iniciativa"}
        </Button>,
      ]}
      bodyStyle={{ padding: "24px 24px 0" }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Título</span>}
          name="titulo"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <Input placeholder="Ex: Expansão para novo mercado" size="large" />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Descrição</span>}
          name="descricao"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <TextArea rows={4} placeholder="Detalhes da iniciativa..." />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Início</span>}
              name="inicio"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Fim</span>}
              name="fim"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   PÁGINA PRINCIPAL DE INICIATIVAS
───────────────────────────────────────────────────────────────────────────── */
const Iniciativas = () => {
  const [iniciativas, setIniciativas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterObjetivo, setFilterObjetivo] = useState("TODOS");
  const [filterDirecao, setFilterDirecao] = useState("TODOS");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingIniciativa, setEditingIniciativa] = useState(null);
  const [objetivos, setObjetivos] = useState([]);
  const [direcoes, setDirecoes] = useState([]);

  const userStored = JSON.parse(localStorage.getItem("user")) || {};

  useEffect(() => {
    document.title = "Gestão de Iniciativas | Dashboard";
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      const [objs, dirs] = await Promise.all([
        Api.ListObjetivo().catch(() => []),
        Api.ListDirecao().catch(() => []),
      ]);
      setObjetivos(Array.isArray(objs) ? objs : []);
      setDirecoes(Array.isArray(dirs) ? dirs : []);
    } catch {
      toast.error("Erro ao carregar objetivos ou direções.");
    }
  };

  const loadIniciativas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListIniciativa();
      const list = Array.isArray(response) ? response : [];
      setIniciativas(list);
    } catch {
      toast.error("Erro ao carregar iniciativas.");
      setIniciativas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIniciativas();
  }, [loadIniciativas]);

  const openFormModal = (iniciativa = null) => {
    setEditingIniciativa(iniciativa);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingIniciativa(null);
  };

  const handleFormSuccess = () => {
    loadIniciativas();
  };

  // Estatísticas
  const totalIniciativas = iniciativas.length;
  const mediaProgresso =
    totalIniciativas > 0
      ? (
          iniciativas.reduce((acc, ini) => acc + parseFloat(ini.percentagem || 0), 0) /
          totalIniciativas
        ).toFixed(1)
      : "0.0";

  // Filtragem
  const filtered = iniciativas.filter((ini) => {
    const matchSearch =
      !search ||
      ini.titulo?.toLowerCase().includes(search.toLowerCase()) ||
      ini.descricao?.toLowerCase().includes(search.toLowerCase());
    const matchObjetivo =
      filterObjetivo === "TODOS" || ini.id_objectivo === parseInt(filterObjetivo);
    const matchDirecao =
      filterDirecao === "TODOS" || ini.id_direcao === parseInt(filterDirecao);
    return matchSearch && matchObjetivo && matchDirecao;
  });

  const columns = [
    {
      title: "Iniciativa",
      dataIndex: "titulo",
      key: "titulo",
      width: 280,
      render: (titulo, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            shape="square"
            size="large"
            style={{
              backgroundColor: "#e6f4ff",
              color: "#1677ff",
              borderRadius: 8,
            }}
            icon={<FaLightbulb />}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 4 }}>
              {titulo || <Text type="secondary">Sem título</Text>}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.descricao?.length > 50
                ? record.descricao.substring(0, 50) + "..."
                : record.descricao || "—"}
            </Text>
          </div>
        </div>
      ),
    },
     
   
    {
      title: "Período",
      key: "periodo",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.inicio)}
          </Text>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.fim)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Progresso",
      dataIndex: "percentagem",
      key: "percentagem",
      width: 180,
      render: (percentagem) => {
        const valor = parseFloat(percentagem) || 0;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress
              percent={valor}
              size="small"
              status={valor === 100 ? "success" : "active"}
              style={{ flex: 1 }}
              strokeColor={{
                "0%": "#1677ff",
                "100%": "#52c41a",
              }}
            />
            <Text strong style={{ minWidth: 45, textAlign: "right" }}>
              {valor}%
            </Text>
          </div>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="Editar iniciativa">
          <Button
            type="text"
            icon={<FaEdit />}
            onClick={() => openFormModal(record)}
            style={{ color: "#1677ff" }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Layout className="iniciativas-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho com gradiente */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaLightbulb style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Gestão de Iniciativas
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Alinhe as iniciativas aos objetivos estratégicos
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<FaPlus />}
                onClick={() => openFormModal()}
                style={{
                  backgroundColor: "#fff",
                  color: "#1677ff",
                  border: "none",
                  fontWeight: 600,
                  borderRadius: 30,
                  padding: "0 24px",
                  height: 48,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                Nova Iniciativa
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12} lg={8}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Total de Iniciativas</span>}
                value={totalIniciativas}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#e6f4ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaLightbulb style={{ fontSize: 24, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Progresso Médio</span>}
                value={mediaProgresso}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#52c41a" }}
                suffix="%"
                prefix={
                  <div
                    style={{
                      backgroundColor: "#f6ffed",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaChartLine style={{ fontSize: 24, color: "#52c41a" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Objetivos Ativos</span>}
                value={objetivos.length}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#722ed1" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#f9f0ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaBullseye style={{ fontSize: 24, color: "#722ed1" }} />
                  </div>
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Barra de ferramentas: pesquisa e filtros */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={12} lg={8}>
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por título ou descrição..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
            <Col xs={24} md={12} lg={7}>
              <Select
                size="large"
                style={{ width: "100%", borderRadius: 30 }}
                value={filterObjetivo}
                onChange={setFilterObjetivo}
                suffixIcon={<FaFilter style={{ color: "#1677ff" }} />}
                placeholder="Filtrar por objetivo"
              >
                <Option value="TODOS">Todos os objetivos</Option>
                {objetivos.map((obj) => (
                  <Option key={obj.id} value={obj.id}>
                    {obj.titulo}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={12} lg={9}>
              <Select
                size="large"
                style={{ width: "100%", borderRadius: 30 }}
                value={filterDirecao}
                onChange={setFilterDirecao}
                suffixIcon={<FaFilter style={{ color: "#1677ff" }} />}
                placeholder="Filtrar por direção"
              >
                <Option value="TODOS">Todas as direções</Option>
                {direcoes.map((dir) => (
                  <Option key={dir.id} value={dir.id}>
                    {dir.descricao}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Tabela de iniciativas */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} iniciativas`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhuma iniciativa encontrada" }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "table-row-light" : "table-row-dark"
            }
          />
        </Card>

        {/* Modal de formulário */}
        <IniciativaFormModal
          open={formModalOpen}
          onClose={closeFormModal}
          onSuccess={handleFormSuccess}
          editingIniciativa={editingIniciativa}
          userStored={userStored}
        />
      </Content>
    </Layout>
  );
};

export default Iniciativas;